import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Bot, KeyRound, Loader2, Save, CheckCircle2, Trash2, Sparkles, PlayCircle,
  Info,
} from "lucide-react";

const OPENAI_MODELS = [
  "gpt-realtime",
  "gpt-realtime-mini",
  "gpt-realtime-translate",
  "gpt-realtime-whisper",
];
const OPENAI_VOICES = ["alloy", "verse", "cedar", "marin", "ash", "ballad", "coral"];

export default function VoiceAgentsPage() {
  const [state, setState] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingOpenai, setTestingOpenai] = useState(false);
  const [testingEleven, setTestingEleven] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/voice-agents/master");
      setState(data || {});
    } catch (e) { toast.error("Failed to load voice settings"); }
  }, []);

  useEffect(() => { (async () => { setLoading(true); await load(); setLoading(false); })(); }, [load]);

  const onChange = (k, v) => setState((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    const body = {
      provider: state.provider || "openai",
      persona_prompt: state.persona_prompt || "",
      openai_model: state.openai_model || "gpt-realtime",
      openai_voice: state.openai_voice || "alloy",
      elevenlabs_agent_id: state.elevenlabs_agent_id || "",
      elevenlabs_first_message: state.elevenlabs_first_message || "",
    };
    if (state.openai_api_key) body.openai_api_key = state.openai_api_key;
    if (state.elevenlabs_api_key) body.elevenlabs_api_key = state.elevenlabs_api_key;
    try {
      const { data } = await api.put("/voice-agents/master", body);
      setState({ ...data, openai_api_key: "", elevenlabs_api_key: "" });
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const testOpenAI = async () => {
    setTestingOpenai(true);
    try {
      const { data } = await api.post("/voice-agents/openai/token");
      toast.success(`OpenAI ready · ${data.model} / ${data.voice} (ephemeral token issued, expires ${data.expires_in_seconds}s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "OpenAI test failed");
    } finally { setTestingOpenai(false); }
  };

  const testElevenLabs = async () => {
    setTestingEleven(true);
    try {
      const { data } = await api.get("/voice-agents/elevenlabs/signed-url");
      toast.success(`ElevenLabs ready · agent ${data.agent_id} (signed URL issued)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "ElevenLabs test failed");
    } finally { setTestingEleven(false); }
  };

  if (loading) return <div className="p-8 flex items-center gap-2 text-zinc-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-5xl" data-testid="voice-agents-page">
      <div className="pb-4 border-b border-zinc-200">
        <div className="label-overline">Admin · Integrations · AI Voice Agents</div>
        <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">AI Voice Agents</h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
          Realtime voice assistant for logged-in admin users. Pick a provider, paste your key,
          and the platform will mint short-lived browser tokens so your master key never leaves
          the server. Phone-call answering (via 3CX) will be wired in a later phase.
        </p>
      </div>

      {/* Provider + persona */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-zinc-100">
            <Bot className="h-4 w-4 text-zinc-700" />
            <h2 className="font-display font-bold text-lg tracking-tight">Active provider</h2>
            <Badge className="rounded-sm bg-zinc-100 text-zinc-800 border-zinc-200 ml-2">
              {state.provider === "elevenlabs" ? "ElevenLabs" : "OpenAI Realtime"}
            </Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="label-overline">Provider</Label>
              <Select value={state.provider || "openai"}
                onValueChange={(v) => onChange("provider", v)}>
                <SelectTrigger className="rounded-sm" data-testid="va-provider">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="openai">OpenAI Realtime</SelectItem>
                  <SelectItem value="elevenlabs">ElevenLabs Conversational AI</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="label-overline">Persona / system prompt</Label>
              <Textarea className="rounded-sm text-[12px]" rows={3} maxLength={4000}
                placeholder="You are a helpful, concise voice assistant for the Iceberg Digital team. Speak naturally; keep answers short unless asked to elaborate."
                value={state.persona_prompt || ""}
                onChange={(e) => onChange("persona_prompt", e.target.value)}
                data-testid="va-persona-prompt" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* OpenAI */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-zinc-100">
            <KeyRound className="h-4 w-4 text-zinc-700" />
            <h2 className="font-display font-bold text-lg tracking-tight">OpenAI Realtime</h2>
            {state.openai_configured && (
              <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200 ml-2">
                <CheckCircle2 className="h-3 w-3 mr-1" /> key saved
              </Badge>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5 md:col-span-2">
              <Label className="label-overline">API key</Label>
              <Input type="password" className="rounded-sm font-mono text-[12px]"
                placeholder={state.openai_configured ? "•••••••••• (saved)" : "sk-proj-..."}
                value={state.openai_api_key || ""}
                onChange={(e) => onChange("openai_api_key", e.target.value)}
                data-testid="va-openai-key" />
              {state.openai_configured && !state.openai_api_key && (
                <p className="text-[10px] text-zinc-500">Leave blank to keep the saved key.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="label-overline">Model</Label>
              <Select value={state.openai_model || "gpt-realtime"}
                onValueChange={(v) => onChange("openai_model", v)}>
                <SelectTrigger className="rounded-sm" data-testid="va-openai-model"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {OPENAI_MODELS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="label-overline">Voice</Label>
              <Select value={state.openai_voice || "alloy"}
                onValueChange={(v) => onChange("openai_voice", v)}>
                <SelectTrigger className="rounded-sm" data-testid="va-openai-voice"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {OPENAI_VOICES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
            <Button type="button" variant="outline" size="sm" className="rounded-sm"
              onClick={testOpenAI} disabled={testingOpenai || !state.openai_configured}
              data-testid="va-openai-test">
              {testingOpenai ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <PlayCircle className="h-3.5 w-3.5 mr-1" />}
              Mint test token
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ElevenLabs */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-zinc-100">
            <Sparkles className="h-4 w-4 text-zinc-700" />
            <h2 className="font-display font-bold text-lg tracking-tight">ElevenLabs Conversational AI</h2>
            {state.elevenlabs_configured && (
              <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200 ml-2">
                <CheckCircle2 className="h-3 w-3 mr-1" /> key saved
              </Badge>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5 md:col-span-2">
              <Label className="label-overline">API key</Label>
              <Input type="password" className="rounded-sm font-mono text-[12px]"
                placeholder={state.elevenlabs_configured ? "•••••••••• (saved)" : "xi-..."}
                value={state.elevenlabs_api_key || ""}
                onChange={(e) => onChange("elevenlabs_api_key", e.target.value)}
                data-testid="va-eleven-key" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-overline">Agent ID</Label>
              <Input className="rounded-sm font-mono text-[12px]" placeholder="agent_xxxx"
                value={state.elevenlabs_agent_id || ""}
                onChange={(e) => onChange("elevenlabs_agent_id", e.target.value)}
                data-testid="va-eleven-agent" />
              <p className="text-[10px] text-zinc-500">Create an agent at elevenlabs.io → Conversational AI → Agents.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="label-overline">First message (override)</Label>
              <Input className="rounded-sm text-[12px]" placeholder="Hi, how can I help?"
                value={state.elevenlabs_first_message || ""}
                onChange={(e) => onChange("elevenlabs_first_message", e.target.value)}
                data-testid="va-eleven-first-message" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
            <Button type="button" variant="outline" size="sm" className="rounded-sm"
              onClick={testElevenLabs} disabled={testingEleven || !state.elevenlabs_configured}
              data-testid="va-eleven-test">
              {testingEleven ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <PlayCircle className="h-3.5 w-3.5 mr-1" />}
              Mint signed URL
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-sm border-zinc-200 bg-zinc-50">
        <CardContent className="p-6 space-y-2">
          <div className="flex items-center gap-2">
            <Info className="h-4 w-4 text-zinc-700" />
            <h2 className="font-display font-bold text-sm tracking-tight">Next steps</h2>
          </div>
          <ul className="list-disc list-inside text-[12px] text-zinc-700 space-y-1">
            <li>Backend brokers ephemeral tokens / signed URLs — master keys never leave the server.</li>
            <li>Coming next: in-browser <strong>Voice Assistant</strong> page that connects via WebRTC and lets you talk in real time.</li>
            <li>3CX phone bridge (sales/support call handling) is a separate phase — needs a SIP↔WebRTC gateway.</li>
            <li>NVIDIA PersonaPlex is on hold pending GPU host decision.</li>
          </ul>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
        <Button type="button" size="sm" className="rounded-sm" onClick={save}
          disabled={saving} data-testid="va-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
          Save settings
        </Button>
      </div>
    </div>
  );
}
